import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Stat = ({ label, value }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-1 font-display text-lg text-slate-100">{value}</p>
  </div>
);

/** Owner-only: close the whole company. 30-day grace by default, immediate on request. */
export const CloseCompanyPanel = () => {
  const { logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [reason, setReason] = useState("");
  const [immediate, setImmediate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const load = () =>
    api.get("/account/deletion-preview").then(({ data }) => setPreview(data)).catch(() => setPreview(null));

  useEffect(() => { load(); }, []);

  const close = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/account/close", { confirm_name: confirmName, reason, immediate });
      if (data.status === "deleted") {
        await logout();
        navigate("/");
        return;
      }
      setDone(`Account closed. It will be permanently deleted on ${String(data.purge_at).slice(0, 10)}.`);
      setOpen(false);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/account/close/undo");
      setDone("");
      await load();
      await refresh();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!preview) return null;

  if (preview.scheduled_at) {
    return (
      <div data-testid="close-company-scheduled" className="rounded-xl border border-red-500/40 bg-red-500/10 p-6">
        <p className="font-mono text-[11px] uppercase tracking-widest text-red-300">Account closing</p>
        <p className="mt-2 text-sm text-slate-100">
          {preview.company_name} is scheduled for permanent deletion on{" "}
          <span className="font-semibold">{String(preview.purge_at).slice(0, 10)}</span>. Until then
          everything is locked but nothing has been erased.
        </p>
        {error && <p data-testid="close-company-error" className="mt-3 text-sm text-red-300">{error}</p>}
        <Button data-testid="close-company-undo" disabled={busy} onClick={undo}
          className="mt-4 bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Keep my account — cancel deletion"}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="close-company-card" className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-300" />
        <p className="font-mono text-[11px] uppercase tracking-widest text-red-300">Close this account</p>
      </div>
      {done && <p data-testid="close-company-done" className="mt-3 text-sm text-emerald-400">{done}</p>}

      {!open ? (
        <>
          <p className="mt-3 text-sm text-slate-400">
            Closing {preview.company_name} deletes the company and everything under it — every trip,
            invoice, vehicle, chauffeur and team login. We email you a full archive of your trips and
            invoices first.
          </p>
          <Button data-testid="close-company-open" variant="outline" onClick={() => setOpen(true)}
            className="mt-4 border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200">
            <Trash2 className="mr-2 h-4 w-4" /> Close my account
          </Button>
        </>
      ) : (
        <div className="mt-4 space-y-5">
          <div className="grid grid-cols-2 gap-4 rounded-lg border border-ink-600 bg-ink-900/60 p-4 sm:grid-cols-4">
            <Stat label="Trips" value={preview.trips} />
            <Stat label="Invoices" value={preview.invoices} />
            <Stat label="Vehicles" value={preview.vehicles} />
            <Stat label="Chauffeurs" value={preview.chauffeurs} />
            <Stat label="Airport manifests" value={preview.airport_manifests} />
            <Stat label="Team logins" value={preview.team} />
            <Stat label="Unpaid invoices" value={`${preview.unpaid_invoices} · ${money(preview.unpaid_total)}`} />
            <Stat label="Trips next 7 days" value={preview.upcoming_trips} />
          </div>

          {(preview.unpaid_invoices > 0 || preview.upcoming_trips > 0) && (
            <p data-testid="close-company-warning" className="rounded-lg border border-amber-400/40 bg-amber-400/10 p-4 text-sm text-amber-100">
              Read this first: you still have {preview.unpaid_invoices} unpaid invoice
              {preview.unpaid_invoices === 1 ? "" : "s"} worth {money(preview.unpaid_total)} and{" "}
              {preview.upcoming_trips} trip{preview.upcoming_trips === 1 ? "" : "s"} booked in the next
              7 days. Closing now wipes those records and your passengers lose their booking and
              tracking links. Collect what you are owed and see those trips through first.
            </p>
          )}

          {preview.has_subscription && (
            <p className="text-sm text-slate-400">
              Your {preview.plan} subscription will be cancelled automatically — you will not be billed again.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {[[false, `Close in ${preview.grace_days} days`], [true, "Delete permanently now"]].map(([v, label]) => (
              <button key={String(v)} type="button" data-testid={`close-mode-${v ? "now" : "grace"}`}
                onClick={() => setImmediate(v)}
                className={`rounded-lg border px-4 py-2 text-left text-sm transition-colors ${
                  immediate === v ? "border-red-500/50 bg-red-500/10 text-red-200"
                    : "border-ink-600 text-slate-400 hover:text-slate-200"
                }`}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            {immediate
              ? "Everything is erased the moment you confirm. There is no way back."
              : `Your account locks immediately and is erased after ${preview.grace_days} days. You can cancel any time before then.`}
          </p>

          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Why are you leaving? (optional — it helps us)
            </label>
            <Textarea data-testid="close-company-reason" value={reason} rows={2}
              onChange={(e) => setReason(e.target.value)}
              className="border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
          </div>

          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Type <span className="text-red-300">{preview.company_name}</span> to confirm
            </label>
            <Input data-testid="close-company-confirm-name" value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
          </div>

          {error && <p data-testid="close-company-error" className="text-sm text-red-400">{error}</p>}

          <div className="flex flex-wrap gap-2">
            <Button data-testid="close-company-submit" disabled={busy || !confirmName.trim()} onClick={close}
              className="bg-red-500 text-white hover:bg-red-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : immediate ? "Delete everything now" : "Close my account"}
            </Button>
            <Button data-testid="close-company-cancel" variant="outline" disabled={busy}
              onClick={() => { setOpen(false); setError(""); setConfirmName(""); }}
              className="border-ink-600 bg-transparent text-slate-300">
              Never mind
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
